import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { observer } from "mobx-react-lite";
import { useStores } from "hooks";
import {
    ModalBody,
    ModalTitle,
    SectionTitle,
    SectionLabel,
    Divider,
    ToggleGrid,
    ToggleItem,
    ToggleSwitch,
    HiddenCheckbox,
    MasterToggleButton,
    MasterToggleDot,
    VoicePickerRow,
    VoiceOption,
    AvatarCircle,
    AvatarImg,
    VoiceDetails,
    VoiceName,
    VoiceDescription,
    SampleButton,
    Overlay,
    Panel,
    CloseButton,
} from "./Styles";

import kyleAvatar from "assets/images/va_avatars/kyle.png";
import kenAvatar from "assets/images/va_avatars/ken.png";
import kendallAvatar from "assets/images/va_avatars/kendall.png";

const voices = [
    { id: "kendall", name: "Kendall", description: "Female voice", avatar: kendallAvatar },
    { id: "kyle", name: "Kyle", description: "Male voice", avatar: kyleAvatar },
    { id: "ken", name: "Ken", description: "Male voice", avatar: kenAvatar },
];

const sfxPrefs = [
    { key: "countdownClock", label: "Countdown clock" },
    { key: "yourBid", label: "Your bid confirmation" },
    { key: "externalBid", label: "External bids" },
    { key: "outbidAlert", label: "Outbid alert" },
    { key: "newMessage", label: "Clerk messages" },
    { key: "pauseMusic", label: "Pause music" },
];

const voicePrefs = [
    { key: "voiceNewItem", label: "New item on block" },
    { key: "voiceOutbid", label: "You've been outbid" },
    { key: "voiceTimeExtended", label: "Time extended" },
    { key: "voiceOutcome", label: "Outcome announcements" },
];

const AudioSettingsModal = observer(({ isOpen, onClose }) => {
    const { VirtualAuctionStore } = useStores();
    const {
        sfxEnabled, voiceEnabled, setSfxEnabled, setVoiceEnabled,
        soundPrefs, setSoundPref,
        voice, setVoiceOver, playVoiceOver,
    } = VirtualAuctionStore;

    const [playingVoice, setPlayingVoice] = useState(null);
    const panelRef = useRef(null);

    // Close on Escape
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isOpen, onClose]);

    // Close on outside click
    const handleOverlayClick = (e) => {
        if (panelRef.current && !panelRef.current.contains(e.target)) {
            onClose();
        }
    };

    const playVoiceSample = async (e, id) => {
        e.stopPropagation();
        setPlayingVoice(id);
        try {
            await playVoiceOver("sample.mp3", id);
        } finally {
            setTimeout(() => setPlayingVoice((cur) => (cur === id ? null : cur)), 3000);
        }
    };

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <Overlay onClick={handleOverlayClick}>
            <Panel ref={panelRef}>
                <CloseButton onClick={onClose} aria-label="Close">
                    <i className="fa fa-times" />
                </CloseButton>

                <ModalBody>
                    <ModalTitle>
                        <i className="fa fa-cog" /> Audio Settings
                    </ModalTitle>

                    {/* SFX Section */}
                    <div>
                        <SectionTitle>
                            <SectionLabel>Sound Effects</SectionLabel>
                            <MasterToggleButton
                                active={sfxEnabled}
                                onClick={() => setSfxEnabled(!sfxEnabled)}
                                aria-label={`SFX ${sfxEnabled ? "enabled" : "disabled"}`}
                            >
                                <span>SFX</span>
                                <MasterToggleDot active={sfxEnabled} />
                            </MasterToggleButton>
                        </SectionTitle>
                        <Divider />
                        <ToggleGrid>
                            {sfxPrefs.map(({ key, label }) => (
                                <ToggleItem key={key} disabled={!sfxEnabled}>
                                    <HiddenCheckbox
                                        type="checkbox"
                                        checked={soundPrefs[key]}
                                        onChange={(e) => setSoundPref(key, e.target.checked)}
                                    />
                                    <ToggleSwitch checked={soundPrefs[key]} />
                                    {label}
                                </ToggleItem>
                            ))}
                        </ToggleGrid>
                    </div>

                    {/* Voice Section */}
                    <div>
                        <SectionTitle>
                            <SectionLabel>Voice Announcements</SectionLabel>
                            <MasterToggleButton
                                active={voiceEnabled}
                                onClick={() => setVoiceEnabled(!voiceEnabled)}
                                aria-label={`Voice ${voiceEnabled ? "enabled" : "disabled"}`}
                            >
                                <span>Voice</span>
                                <MasterToggleDot active={voiceEnabled} />
                            </MasterToggleButton>
                        </SectionTitle>
                        <Divider />
                        <ToggleGrid>
                            {voicePrefs.map(({ key, label }) => (
                                <ToggleItem key={key} disabled={!voiceEnabled}>
                                    <HiddenCheckbox
                                        type="checkbox"
                                        checked={soundPrefs[key]}
                                        onChange={(e) => setSoundPref(key, e.target.checked)}
                                    />
                                    <ToggleSwitch checked={soundPrefs[key]} />
                                    {label}
                                </ToggleItem>
                            ))}
                        </ToggleGrid>
                    </div>

                    {/* Voice Selection */}
                    <div>
                        <SectionTitle>
                            <SectionLabel>Voice Selection</SectionLabel>
                        </SectionTitle>
                        <Divider />
                        <VoicePickerRow>
                            {voices.map((v) => {
                                const isActive = voice === v.id;
                                const isPlaying = playingVoice === v.id;
                                return (
                                    <VoiceOption key={v.id} isActive={isActive} onClick={() => setVoiceOver(v.id)}>
                                        <AvatarCircle isActive={isActive}>
                                            <AvatarImg src={v.avatar} alt={`${v.name} avatar`} />
                                        </AvatarCircle>
                                        <VoiceDetails>
                                            <VoiceName isActive={isActive}>{v.name}</VoiceName>
                                            <VoiceDescription>{v.description}</VoiceDescription>
                                        </VoiceDetails>
                                        <SampleButton
                                            type="button"
                                            isPlaying={isPlaying}
                                            onClick={(e) => playVoiceSample(e, v.id)}
                                            title="Play sample"
                                        >
                                            <i className={isPlaying ? "fa fa-volume-up" : "fa fa-headphones"} />
                                            <span>Sample</span>
                                        </SampleButton>
                                    </VoiceOption>
                                );
                            })}
                        </VoicePickerRow>
                    </div>
                </ModalBody>
            </Panel>
        </Overlay>,
        document.getElementById("root")
    );
});

export default AudioSettingsModal;
